import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Pagination, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from '@mui/material';
import { Download, Eye, RotateCcw } from 'lucide-react';
import {
  branchDeposits, branchWithdrawals, branchStaff, downloadBranchReport,
} from '../../services/branchApi';
import type { TxnFilters, BranchStaff } from '../../services/branchApi';
import TxnDetailModal from './TxnDetailModal';

const money = (v: any) => (v == null ? '-' : `₹${Number(v).toLocaleString()}`);
const DEP_STATUS = ['pending', 'approved', 'rejected'];
const WD_STATUS = ['In Queue', 'Done', 'Rejected'];
const depColor: any = { approved: 'success', pending: 'warning', rejected: 'error' };
const wdColor: any = { Done: 'success', 'In Queue': 'warning', Pending: 'warning', Rejected: 'error' };

const emptyFilters: TxnFilters = { from: '', to: '', status: '', method: '', agentId: '', search: '' };

export default function BranchTxnReport({ kind }: { kind: 'deposits' | 'withdrawals' }) {
  const isDep = kind === 'deposits';
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TxnFilters>({ ...emptyFilters });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [staff, setStaff] = useState<BranchStaff[]>([]);
  const [detail, setDetail] = useState<any | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { branchStaff().then((r) => setStaff(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: TxnFilters = { ...filters, page, limit: 25 };
      const r: any = isDep ? await branchDeposits(params) : await branchWithdrawals(params);
      setRows(r.data || []);
      setTotalPages(Math.max(1, Math.ceil((r.meta?.total || 0) / (r.meta?.limit || 25))));
    } finally { setLoading(false); }
  }, [filters, page, isDep]);
  useEffect(() => { load(); }, [load]);

  const set = (k: keyof TxnFilters, v: any) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const reset = () => { setPage(1); setFilters({ ...emptyFilters }); };
  const download = async () => {
    setDownloading(true);
    try { await downloadBranchReport(kind, filters); } catch (e: any) { alert(e.message); } finally { setDownloading(false); }
  };

  const cols = ['SN#', 'Requested', 'User', 'Amount', isDep ? 'Approved' : 'Paid', 'Status', 'Bank / UPI', 'Action By', 'Action Date', 'Remark', 'View'];

  return (
    <Box>
      {/* filter bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }} alignItems="center">
        <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
          value={filters.from} onChange={(e) => set('from', e.target.value)} />
        <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
          value={filters.to} onChange={(e) => set('to', e.target.value)} />
        <TextField select size="small" label="Status" sx={{ minWidth: 130 }}
          value={filters.status} onChange={(e) => set('status', e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {(isDep ? DEP_STATUS : WD_STATUS).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Method" sx={{ minWidth: 120 }}
          value={filters.method} onChange={(e) => set('method', e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="UPI">UPI</MenuItem>
          <MenuItem value="BANK">Bank</MenuItem>
        </TextField>
        <TextField select size="small" label="Handled by" sx={{ minWidth: 150 }}
          value={filters.agentId} onChange={(e) => set('agentId', e.target.value)}>
          <MenuItem value="">Any agent</MenuItem>
          {staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.username} ({s.role})</MenuItem>)}
        </TextField>
        <TextField size="small" label="Search user / bank / ref" sx={{ minWidth: 200 }}
          value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <IconButton size="small" title="Reset filters" onClick={reset}><RotateCcw size={16} /></IconButton>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" size="small" startIcon={<Download size={16} />} onClick={download} disabled={downloading}>
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
      </Stack>

      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>{cols.map((h) => <TableCell key={h} sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const id = isDep ? r.deposit_id : r.id;
                  const done = isDep ? r.status === 'approved' : r.status === 'Done';
                  return (
                    <TableRow key={`${kind}-${id}`} hover>
                      <TableCell>{(page - 1) * 25 + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{new Date(r.created_at || r.date).toLocaleString()}</TableCell>
                      <TableCell>{r.username}</TableCell>
                      <TableCell>{money(r.amount)}</TableCell>
                      <TableCell>{done ? money(r.approved_amount ?? r.amount) : money(0)}</TableCell>
                      <TableCell><Chip size="small" color={(isDep ? depColor : wdColor)[r.status]} label={r.status} /></TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.upi_id || `${r.bank_name || ''} ${r.account_number || ''}`.trim() || '-'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.action_by || '-'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.handled_at ? new Date(r.handled_at).toLocaleString() : '-'}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark || ''}>{r.remark || '-'}</TableCell>
                      <TableCell><IconButton size="small" onClick={() => setDetail(r)}><Eye size={16} /></IconButton></TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>No {kind} match these filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      <TxnDetailModal open={!!detail} onClose={() => setDetail(null)} kind={isDep ? 'deposit' : 'withdraw'} row={detail} />
    </Box>
  );
}
